import React, { useState, useEffect } from 'react';
import { Shield, LogIn, LogOut, Sparkles, Clock, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const SESSION_ENDED_COUNTDOWN = 5;

const ACCESS_REMOVED_STATUSES = ['access_revoked', 'account_deactivated'];

function getModalCopy(accountStatus) {
    if (ACCESS_REMOVED_STATUSES.includes(accountStatus)) {
        return {
            title: 'Account Access Revoked',
            body: 'Your access to this dashboard has been removed by your administrator.\n\nYou will be signed out automatically.',
        };
    }
    return {
        title: 'Session ended',
        body: 'For security reasons, your session has ended. Please log in again to continue using the application.',
    };
}

/**
 * Full-screen overlay when the session expires or is invalidated.
 * Matches post-login dialogs (blur circle, shield, sparkles). Storage is already cleared;
 * user continues only via full navigation to login.
 * Auto-redirects after a 5-second countdown.
 */
export default function SessionEndedModal({ open, onLoginAgain, accountStatus }) {
    const [count, setCount] = useState(SESSION_ENDED_COUNTDOWN);
    const { title, body } = getModalCopy(accountStatus);
    const isAccessRemoved = ACCESS_REMOVED_STATUSES.includes(accountStatus);

    // Reset and start countdown whenever the modal opens
    useEffect(() => {
        if (!open) {
            setCount(SESSION_ENDED_COUNTDOWN);
            return;
        }
        if (count <= 0) {
            onLoginAgain();
            return;
        }
        const id = setTimeout(() => setCount((c) => c - 1), 1000);
        return () => clearTimeout(id);
    }, [open, count, onLoginAgain]);

    return (
        <Dialog open={open}>
            <DialogContent
                className="sm:max-w-md border-0 shadow-xl backdrop-blur-sm bg-card/95 [&>button:last-child]:hidden"
                aria-describedby={undefined}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <DialogTitle className="sr-only">Session ended</DialogTitle>

                <div className="space-y-5 text-center pt-2">
                    <div className="relative mx-auto flex h-28 w-28 items-center justify-center">
                        <div
                            className="absolute inset-0 rounded-full bg-primary/10 blur-xl"
                            aria-hidden
                        />
                        <Sparkles
                            className="absolute -top-1 -right-1 h-5 w-5 text-primary/40"
                            aria-hidden
                        />
                        <Sparkles
                            className="absolute -bottom-0.5 -left-1 h-4 w-4 text-primary/30"
                            aria-hidden
                        />

                        <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-primary/15 ring-4 ring-primary/10">
                            <div className="relative flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-md">
                                <Shield className="h-7 w-7" aria-hidden />
                            </div>
                            {isAccessRemoved ? (
                                <div
                                    className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full bg-destructive ring-2 ring-destructive/20 shadow-md"
                                    aria-hidden
                                >
                                    <X className="h-4 w-4 text-white" />
                                </div>
                            ) : (
                                <div
                                    className="absolute -bottom-1 -right-1 flex h-9 w-9 items-center justify-center rounded-full bg-background ring-2 ring-primary/15 shadow-md"
                                    aria-hidden
                                >
                                    <LogOut className="h-4 w-4 text-primary" />
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <h2 className="text-xl font-semibold text-foreground leading-tight px-1">
                            {title}
                        </h2>
                        <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed whitespace-pre-line">
                            {body}
                        </p>
                    </div>

                    <div className="flex justify-center">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-muted text-sm text-muted-foreground">
                            <Clock className="h-4 w-4 shrink-0" aria-hidden />
                            Signing out in{' '}
                            <span className="font-semibold text-primary">{count}</span>{' '}
                            seconds...
                        </div>
                    </div>

                    {!isAccessRemoved && (
                        <Button className="w-full font-semibold gap-2" size="lg" onClick={onLoginAgain}>
                            <LogIn className="h-5 w-5" aria-hidden />
                            Login again
                        </Button>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
